import React, { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';

export function VariableInput({
    value,
    onChange,
    variables = [],
    multiline = false,
    noFocusRing = false,
    fullHeight = false,
    truncate = false,
    rightElement,
    placeholder = "Digite sua mensagem...",
    className,
    ...props
}) {
    const editorRef = useRef(null);
    const containerRef = useRef(null);
    const [open, setOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [isFocused, setIsFocused] = useState(false);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    // Store cursor position to restore it or insert variable at correct place
    const cursorRef = useRef(null);
    // Track the last HTML to avoid unnecessary re-renders/cursor jumps loop
    const lastHtmlRef = useRef("");

    // --- Helpers for highlighting ---

    const getVarType = (val) => {
        if (val.startsWith('contact.') || val === 'now') return 'system';
        return 'custom';
    };

    /**
     * Convert plain text like "Hello {{name}}" to HTML like "Hello <span...>name</span>"
     */
    const textToHtml = useCallback((text) => {
        if (!text) return "";
        // Regex to find {{...}}
        return text.replace(/\{\{([^}]+)\}\}/g, (match, varName) => {
            const type = getVarType(varName);
            const styleClass = type === 'system'
                ? "bg-cyan-500/20 text-cyan-300 border-cyan-500/30"
                : "bg-indigo-500/20 text-indigo-300 border-indigo-500/30";

            // contentEditable=false makes the chip treated as a single unit
            return `<span class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded mx-0.5 text-xs font-medium border align-middle cursor-default ${styleClass}" contenteditable="false" data-variable="${varName}">${varName}<span data-remove-var="${varName}" class="ml-0.5 cursor-pointer opacity-60 hover:opacity-100 text-[10px] leading-none" contenteditable="false">×</span></span>`;
        });
    }, []);

    /**
     * Convert HTML content back to plain text for onChange
     */
    const htmlToText = useCallback((element) => {
        let text = "";
        element.childNodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                // Sanitize invisible characters injected by contentEditable
                text += node.textContent
                    .replaceAll('\u200B', '')    // zero-width space
                    .replaceAll('\u200C', '')    // zero-width non-joiner
                    .replaceAll('\u200D', '')    // zero-width joiner
                    .replaceAll('\uFEFF', '')    // BOM
                    .replaceAll('\u00AD', '')    // soft hyphen
                    .replaceAll('\u2028', '')    // line separator
                    .replaceAll('\u2029', '')    // paragraph separator
                    .replaceAll('\u00A0', ' ')   // non-breaking space → space
                    .replaceAll('\u202F', ' ')   // narrow no-break space → space
                    .replaceAll('\u205F', ' ')   // medium math space → space
                    .replaceAll('\u3000', ' ')   // ideographic space → space
                    .replaceAll('\u2000', ' ')   // en quad → space
                    .replaceAll('\u2001', ' ')   // em quad → space
                    .replaceAll('\u2002', ' ')   // en space → space
                    .replaceAll('\u2003', ' ')   // em space → space
                    .replaceAll('\u2004', ' ')   // three-per-em space → space
                    .replaceAll('\u2005', ' ')   // four-per-em space → space
                    .replaceAll('\u2006', ' ')   // six-per-em space → space
                    .replaceAll('\u2007', ' ')   // figure space → space
                    .replaceAll('\u2008', ' ')   // punctuation space → space
                    .replaceAll('\u2009', ' ')   // thin space → space
                    .replaceAll('\u200A', ' ');   // hair space → space
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                if (node.hasAttribute('data-variable')) {
                    text += `{{${node.getAttribute('data-variable')}}}`;
                } else if (node.tagName === 'BR') {
                    text += '\n';
                } else if (node.tagName === 'DIV') {
                    // Chrome adds <div><br></div> for lines — only add newline if there's content before
                    if (text.length > 0) text += '\n';
                    text += htmlToText(node);
                } else {
                    text += htmlToText(node);
                }
            }
        });
        return text;
    }, []);

    // --- Synchronization Effect ---

    // Update HTML when value prop changes externally
    useEffect(() => {
        if (editorRef.current) {
            const expectedHtml = textToHtml(value);
            // Only update if clearly different to avoid fighting with cursor
            if (expectedHtml !== lastHtmlRef.current) {
                const currentDerivedText = htmlToText(editorRef.current);
                // Only if content mismatch (external update) or if we need to format braces
                if (currentDerivedText !== value || (editorRef.current.innerText.includes('}}') && !editorRef.current.innerHTML.includes('data-variable'))) {
                    editorRef.current.innerHTML = expectedHtml;
                    lastHtmlRef.current = expectedHtml;

                    // If active, move cursor to end as fallback
                    if (document.activeElement === editorRef.current) {
                        placeCaretAtEnd(editorRef.current);
                    }
                }
            }
        }
    }, [value, textToHtml, htmlToText]);


    // --- Event Handlers ---

    const handleInput = (e) => {
        if (!editorRef.current) return;

        let rawText = htmlToText(editorRef.current);

        // Fix: If text is just newlines or empty, force empty string
        if (!rawText.trim()) {
            rawText = "";
        } else if (!multiline) {
            // Single-line inputs: trim whitespace/newlines that contentEditable may inject
            rawText = rawText.trim();
        }

        onChange({ target: { value: rawText } }); // Synthetic event shape

        // Trigger autocompleter check
        checkTrigger();

        lastHtmlRef.current = editorRef.current.innerHTML;
    };

    const checkTrigger = () => {
        const selection = window.getSelection();
        if (!selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        const node = range.startContainer;
        if (node.nodeType === Node.TEXT_NODE) {
            const textBefore = node.textContent.substring(0, range.startOffset);
            const lastBraces = textBefore.lastIndexOf('{{');

            if (lastBraces !== -1) {
                const textAfterBraces = textBefore.substring(lastBraces + 2);
                if (!textAfterBraces.includes('}}')) {
                    setSearchTerm(textAfterBraces);
                    cursorRef.current = range.cloneRange();

                    // Calculate cursor position for dropdown using temporary marker
                    // This is more reliable than range.getBoundingClientRect() in scrollable contentEditable
                    if (containerRef.current && editorRef.current) {
                        // Insert a temporary marker span at cursor position
                        const marker = document.createElement('span');
                        marker.id = '__cursor_marker__';
                        // Add a zero-width space so the marker has actual dimensions
                        marker.textContent = '\u200B';
                        marker.style.display = 'inline';
                        marker.style.fontSize = 'inherit';
                        marker.style.lineHeight = 'inherit';

                        // Clone range and insert marker
                        const markerRange = range.cloneRange();
                        markerRange.collapse(true); // Collapse to cursor position
                        markerRange.insertNode(marker);

                        // Get marker position (this is always accurate with the zero-width space)
                        const markerRect = marker.getBoundingClientRect();

                        // Remove marker immediately
                        marker.remove();

                        // Restore selection (marker insertion may have disrupted it)
                        const sel = window.getSelection();
                        sel.removeAllRanges();
                        sel.addRange(range);

                        const dropdownHeight = 220;
                        const lineHeight = 20; // Approximate line height in the editor
                        const spaceBelow = window.innerHeight - markerRect.bottom;
                        const spaceAbove = markerRect.top;

                        // Smart positioning: prefer below the cursor line, but go above if not enough space
                        let top;
                        if (spaceBelow >= dropdownHeight) {
                            // Position directly below the cursor with a small gap
                            top = markerRect.bottom + 2;
                        } else if (spaceAbove >= dropdownHeight) {
                            // Position above the cursor line
                            top = markerRect.top - dropdownHeight - 2;
                        } else {
                            // Not enough space either way, choose the side with more room
                            top = spaceBelow > spaceAbove
                                ? markerRect.bottom + 2
                                : Math.max(8, markerRect.top - dropdownHeight);
                        }

                        setDropdownPosition({
                            top,
                            left: Math.max(8, markerRect.left - 10) // Slight offset to align with {{ better
                        });
                    }

                    setOpen(true);
                    return;
                }
            }
        }
        setOpen(false);
    };

    const insertVariable = (variableValue) => {
        if (cursorRef.current) {
            const range = cursorRef.current;
            const node = range.startContainer;

            if (node.nodeType === Node.TEXT_NODE) {
                const fullText = node.textContent;
                const textBeforeCursor = fullText.substring(0, range.startOffset);
                const lastBraces = textBeforeCursor.lastIndexOf('{{');

                if (lastBraces !== -1) {
                    const before = fullText.substring(0, lastBraces);
                    const after = fullText.substring(range.startOffset);

                    const newTextContent = before + `{{${variableValue}}}` + after;
                    node.textContent = newTextContent;

                    const newTotalText = htmlToText(editorRef.current);
                    onChange({ target: { value: newTotalText } });

                    setOpen(false);
                    setSearchTerm('');

                    setTimeout(() => {
                        if (editorRef.current) {
                            editorRef.current.focus();
                            placeCaretAtEnd(editorRef.current);
                        }
                    }, 0);
                    return;
                }
            }
        }

        // Fallback: Append to end
        const currentVal = value || "";
        onChange({ target: { value: currentVal + `{{${variableValue}}}` } });
        setOpen(false);
    };

    const placeCaretAtEnd = (el) => {
        const range = document.createRange();
        const sel = window.getSelection();
        // Garante um nó de texto final ancorável: sem isso, quando o
        // último filho é um pill (contenteditable=false), o caret não
        // aparece e o usuário não consegue digitar/inserir nova variável.
        const last = el.lastChild;
        if (!last || (last.nodeType === Node.ELEMENT_NODE && last.getAttribute?.('contenteditable') === 'false')) {
            const tail = document.createTextNode('\u200B');
            el.appendChild(tail);
            range.setStart(tail, tail.textContent.length);
            range.collapse(true);
        } else {
            range.selectNodeContents(el);
            range.collapse(false);
        }
        sel.removeAllRanges();
        sel.addRange(range);
    };

    /**
     * Coloca o caret imediatamente após um pill, criando um nó de
     * texto invisível se necessário (caso o pill seja o último filho
     * ou esteja imediatamente antes de outro pill).
     */
    const placeCaretAfterPill = (pill) => {
        if (!pill) return;
        let next = pill.nextSibling;
        const needsTail =
            !next ||
            (next.nodeType === Node.ELEMENT_NODE &&
                next.getAttribute?.('contenteditable') === 'false');
        if (needsTail) {
            next = document.createTextNode('\u200B');
            pill.parentNode.insertBefore(next, pill.nextSibling);
        }
        const sel = window.getSelection();
        const range = document.createRange();
        if (next.nodeType === Node.TEXT_NODE) {
            range.setStart(next, next.textContent.length);
        } else {
            range.setStartAfter(pill);
        }
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    };

    const filteredVariables = variables.filter(v =>
        v.value.toLowerCase().includes(searchTerm.toLowerCase()) ||
        v.label.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div
            ref={containerRef}
            className={cn(
                "w-full max-w-full relative bg-background border text-sm text-foreground-strong rounded-md shadow-sm transition-all outline-none cursor-text px-3 group",
                isFocused && !noFocusRing ? "border-green-500 ring-1 ring-green-500/20" : "border-border",
                multiline ? "py-3" : (truncate ? "py-1.5" : "py-0 flex items-center"),
                fullHeight && "h-full flex flex-col",
                rightElement && !multiline && "pr-9",
                className
            )}
            onClick={() => {
                if (editorRef.current) {
                    editorRef.current.focus();
                    // Ensure caret is placed at a valid position — without this,
                    // clicking a field that only contains a contenteditable=false pill
                    // won't produce a visible/usable cursor.
                    const sel = window.getSelection();
                    if (!sel.rangeCount || !editorRef.current.contains(sel.anchorNode) || sel.anchorNode === editorRef.current) {
                        placeCaretAtEnd(editorRef.current);
                    }
                }
            }}
        >
            <div
                ref={editorRef}
                contentEditable
                className={cn(
                    "outline-none bg-transparent leading-normal",
                    truncate
                        ? cn(
                            "block w-full whitespace-nowrap min-h-[20px]",
                            isFocused
                                ? "overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none]"
                                : "overflow-hidden text-ellipsis"
                        )
                        : "w-full whitespace-pre-wrap",
                    fullHeight
                        ? "flex-1 overflow-y-auto custom-scrollbar"
                        : (multiline ? "min-h-[100px] max-h-[120px] overflow-y-auto custom-scrollbar" : (!truncate && "min-h-[20px] overflow-hidden flex items-center flex-wrap"))
                )}
                onInput={handleInput}
                onClick={(e) => {
                    // Handle click on × remove button inside variable pills
                    const removeBtn = e.target.closest?.('[data-remove-var]') || (e.target.hasAttribute?.('data-remove-var') ? e.target : null);
                    if (removeBtn) {
                        e.preventDefault();
                        e.stopPropagation();
                        const pill = removeBtn.closest('[data-variable]') || removeBtn.parentElement;
                        if (pill) {
                            pill.remove();
                            handleInput();
                        }
                        return;
                    }
                    // If clicked directly on a pill (not the × button), place caret after it
                    const pill = e.target.closest?.('[data-variable]') || (e.target.hasAttribute?.('data-variable') ? e.target : null);
                    if (pill) {
                        e.preventDefault();
                        placeCaretAfterPill(pill);
                    }
                }}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                onPaste={(e) => {
                    e.preventDefault();
                    const plain = e.clipboardData.getData('text/plain');
                    const sel = window.getSelection();
                    if (sel.rangeCount) {
                        const range = sel.getRangeAt(0);
                        range.deleteContents();
                        range.insertNode(document.createTextNode(plain));
                        range.collapse(false);
                        sel.removeAllRanges();
                        sel.addRange(range);
                    }
                    handleInput();
                }}
                onKeyDown={(e) => {
                    // Block Enter in single-line mode to prevent <div> injection
                    if (!multiline && e.key === 'Enter') {
                        e.preventDefault();
                        return;
                    }
                    if (e.key === 'Escape') {
                        setOpen(false);
                        return;
                    }

                    // Handle Backspace/Delete for variable pill removal
                    if (e.key === 'Backspace' || e.key === 'Delete') {
                        const selection = window.getSelection();
                        if (!selection.rangeCount) return;

                        const range = selection.getRangeAt(0);

                        // Helper: find nearest variable pill from a node
                        const findPill = (node) => {
                            if (!node) return null;
                            if (node.nodeType === Node.ELEMENT_NODE && node.hasAttribute?.('data-variable')) return node;
                            if (node.parentElement?.hasAttribute?.('data-variable')) return node.parentElement;
                            return null;
                        };

                        // 1. If selection is inside the editor at element level (cursor between child nodes)
                        if (range.startContainer === editorRef.current) {
                            const offset = range.startOffset;
                            const children = editorRef.current.childNodes;
                            // Backspace: check node before cursor
                            if (e.key === 'Backspace' && offset > 0) {
                                const target = findPill(children[offset - 1]);
                                if (target) { e.preventDefault(); target.remove(); handleInput(); return; }
                            }
                            // Backspace at offset 0: check first child
                            if (e.key === 'Backspace' && offset === 0 && children.length > 0) {
                                const target = findPill(children[0]);
                                if (target) { e.preventDefault(); target.remove(); handleInput(); return; }
                            }
                            // Delete: check node at cursor
                            if (e.key === 'Delete' && offset < children.length) {
                                const target = findPill(children[offset]);
                                if (target) { e.preventDefault(); target.remove(); handleInput(); return; }
                            }
                        }

                        // 2. Cursor in a text node adjacent to a pill
                        if (e.key === 'Backspace') {
                            if (range.startOffset === 0 && range.startContainer.nodeType === Node.TEXT_NODE) {
                                const prev = range.startContainer.previousSibling;
                                const target = findPill(prev);
                                if (target) { e.preventDefault(); target.remove(); handleInput(); return; }
                            }
                        }
                        if (e.key === 'Delete') {
                            if (range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset === range.startContainer.textContent.length) {
                                const next = range.startContainer.nextSibling;
                                const target = findPill(next);
                                if (target) { e.preventDefault(); target.remove(); handleInput(); return; }
                            }
                        }

                        // 3. Cursor is inside or on top of a pill itself
                        const pill = findPill(selection.anchorNode);
                        if (pill) {
                            e.preventDefault();
                            pill.remove();
                            handleInput();
                            return;
                        }

                        // 4. Only meaningful content is a single pill (clear all)
                        const meaningfulNodes = Array.from(editorRef.current.childNodes).filter(
                            (n) => !(n.nodeType === Node.TEXT_NODE && !n.textContent.replaceAll('\u200B', '').trim())
                        );
                        if (meaningfulNodes.length === 1 && findPill(meaningfulNodes[0])) {
                            e.preventDefault();
                            editorRef.current.innerHTML = '';
                            handleInput();
                            return;
                        }
                    }
                }}
                spellCheck={false}
                suppressContentEditableWarning={true}
            />

            {/* Right element (e.g., copy button) - absolute positioned, hidden by default */}
            {rightElement && (
                <div
                    className={cn(
                        "absolute right-2 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-200",
                        multiline ? "top-2" : "top-1/2 -translate-y-1/2"
                    )}
                    onClick={(e) => e.stopPropagation()}
                >
                    {rightElement}
                </div>
            )}

            {!value && (
                <div className={cn(
                    "absolute text-foreground-muted pointer-events-none text-sm italic select-none left-3",
                    multiline ? "top-3" : "top-1/2 -translate-y-1/2"
                )}>
                    {placeholder}
                </div>
            )}

            {/* Dropdown positioned at cursor - rendered via portal to escape Dialog transform context */}
            {open && filteredVariables.length > 0 && createPortal(
                <div
                    className="fixed z-[9999] w-[250px] p-0 bg-surface border border-border text-foreground shadow-xl rounded-md"
                    style={{
                        top: dropdownPosition.top,
                        left: Math.max(8, Math.min(dropdownPosition.left, window.innerWidth - 260)),
                        pointerEvents: 'auto'
                    }}
                    onMouseDown={(e) => e.stopPropagation()}
                    onWheel={(e) => e.stopPropagation()}
                >
                    <div
                        className="p-1 max-h-[200px] overflow-y-auto custom-scrollbar"
                        style={{ overscrollBehavior: 'contain' }}
                    >
                        {filteredVariables.map((v) => (
                            <button
                                key={v.value}
                                onClick={() => insertVariable(v.value)}
                                className="w-full text-left px-3 py-2 text-xs hover:bg-surface-active rounded-md flex items-center gap-3 group transition-all"
                            >
                                <span className={cn(
                                    "flex items-center justify-center w-6 h-6 rounded-md border text-[10px] font-mono shrink-0",
                                    v.type === 'system'
                                        ? "bg-cyan-500/10 border-cyan-500/30 text-cyan-400 group-hover:bg-cyan-500/20 group-hover:border-cyan-500/50"
                                        : "bg-indigo-500/10 border-indigo-500/30 text-indigo-400 group-hover:bg-indigo-500/20 group-hover:border-indigo-500/50"
                                )}>
                                    {{ 'system': 'SYS', 'custom': 'VAR' }[v.type] || 'VAR'}
                                </span>
                                <div className="flex flex-col min-w-0">
                                    <span className="font-semibold text-foreground group-hover:text-foreground-strong truncate font-mono">
                                        {v.value}
                                    </span>
                                    <span className="text-[10px] text-foreground-muted group-hover:text-foreground-muted truncate">
                                        {v.label && v.label !== v.value ? v.label : 'Sem descrição'}
                                    </span>
                                </div>
                            </button>
                        ))}
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
